"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useCallback } from "react";
interface UseLongPressInteractionOptions {
  longPressDuration?: number | undefined;
  moveThreshold?: number | undefined;
  onInteractionStart?: (() => void) | undefined;
  hasTouchScreen?: boolean | undefined;
}

/**
 * Hook for handling long press touch interactions.
 * Detects when a user holds their finger on an element and manages the resulting state.
 * Can be used to trigger various actions like showing menus, selections, or other UI responses.
 *
 * @param options Configuration options for the long press behavior, including hasTouchScreen which must be provided externally
 * @returns Object with interaction state and touch event handlers
 */
export default function useLongPressInteraction(t0) {
  const $ = _c(18);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    longPressDuration: t2,
    moveThreshold: t3,
    onInteractionStart,
    hasTouchScreen: t4
  } = options;
  const longPressDuration = t2 === undefined ? 500 : t2;
  const moveThreshold = t3 === undefined ? 10 : t3;
  const hasTouchScreen = t4 === undefined ? false : t4;
  const [isActive, setIsActive] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const longPressTimeout = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  let t5;
  if ($[2] !== onInteractionStart) {
    t5 = () => {
      setIsActive(true);
      setLongPressTriggered(true);
      onInteractionStart?.();
    };
    $[2] = onInteractionStart;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const startInteraction = t5;
  let t6;
  if ($[4] !== hasTouchScreen || $[5] !== longPressDuration || $[6] !== startInteraction) {
    t6 = e => {
      if (!hasTouchScreen) {
        return;
      }
      if (e.cancelable) {
        e.preventDefault();
      }
      touchStartX.current = e.touches[0]?.clientX;
      touchStartY.current = e.touches[0]?.clientY;
      longPressTimeout.current = setTimeout(() => {
        startInteraction();
      }, longPressDuration);
    };
    $[4] = hasTouchScreen;
    $[5] = longPressDuration;
    $[6] = startInteraction;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleTouchStart = t6;
  let t7;
  if ($[8] !== moveThreshold) {
    t7 = e_0 => {
      if (!longPressTimeout.current) {
        return;
      }
      if (e_0.cancelable) {
        e_0.preventDefault();
      }
      const touchX = e_0.touches[0]?.clientX;
      const touchY = e_0.touches[0]?.clientY;
      const deltaX = Math.abs(touchX - touchStartX.current);
      const deltaY = Math.abs(touchY - touchStartY.current);
      if (deltaX > moveThreshold || deltaY > moveThreshold) {
        if (longPressTimeout.current) {
          clearTimeout(longPressTimeout.current);
          longPressTimeout.current = null;
        }
      }
    };
    $[8] = moveThreshold;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const handleTouchMove = t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      if (longPressTimeout.current) {
        clearTimeout(longPressTimeout.current);
        longPressTimeout.current = null;
      }
      setLongPressTriggered(false);
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleTouchEnd = t8;
  let t9;
  if ($[11] !== handleTouchMove || $[12] !== handleTouchStart) {
    t9 = {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: handleTouchEnd
    };
    $[11] = handleTouchMove;
    $[12] = handleTouchStart;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== isActive || $[15] !== longPressTriggered || $[16] !== t9) {
    t10 = {
      isActive,
      setIsActive,
      longPressTriggered,
      touchHandlers: t9
    };
    $[14] = isActive;
    $[15] = longPressTriggered;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}